import React, { useMemo, useState } from "react";
import {
    Layout, Menu, Button, Input, Card, Row, Col, Typography, Space, Tag,
    Select, Modal, Form, Statistic, Divider, Dropdown, Badge, message, ConfigProvider
} from "antd";
import {
    MenuOutlined, SearchOutlined, ArrowRightOutlined,
    FileTextOutlined, SafetyCertificateOutlined, EnvironmentOutlined,
    TeamOutlined, AppstoreOutlined, CheckCircleFilled, UserAddOutlined,
    BarChartOutlined, ExperimentOutlined, BookOutlined, PhoneOutlined,
    MailOutlined, EnvironmentFilled, FacebookFilled, InstagramFilled,
    YoutubeFilled, XOutlined, DownloadOutlined, CalendarOutlined,
    BellOutlined, GlobalOutlined, CompassOutlined, CloseOutlined
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

const { Header, Content, Footer } = Layout;
const { Title, Text, Paragraph } = Typography;

const photos = {
    hero: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=2000&q=85",
    fruit: "https://images.unsplash.com/photo-1619566636858-adf3ef46400b?auto=format&fit=crop&w=900&q=85",
    vegetable: "https://images.unsplash.com/photo-1540420773420-3366772f4999?auto=format&fit=crop&w=900&q=85",
    flower: "https://images.unsplash.com/photo-1490750967868-88aa4486c946?auto=format&fit=crop&w=900&q=85",
    plantation: "https://images.unsplash.com/photo-1511497584788-876760111969?auto=format&fit=crop&w=900&q=85",
    greenhouse: "https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?auto=format&fit=crop&w=900&q=85",
    irrigation: "https://images.unsplash.com/photo-1560493676-04071c5f467b?auto=format&fit=crop&w=900&q=85",
    nursery: "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?auto=format&fit=crop&w=900&q=85",
    farmer: "https://images.unsplash.com/photo-1592982537447-6f2a6a0b3f8a?auto=format&fit=crop&w=900&q=85"
};

const quickServices = [
    { title: "Apply for Subsidy", sub: "Explore available assistance", icon: <SafetyCertificateOutlined />, color: "#4c9c68" },
    { title: "Check Application Status", sub: "Track your application", icon: <SearchOutlined />, color: "#2589cf" },
    { title: "Micro Irrigation", sub: "Drip & sprinkler schemes", icon: <ExperimentOutlined />, color: "#6179d8" },
    { title: "Planting Material", sub: "Find quality planting stock", icon: <AppstoreOutlined />, color: "#dc8a4b" },
    { title: "Farmer Registration", sub: "Register as a farmer", icon: <TeamOutlined />, color: "#239b9a" },
    { title: "Find Horticulture Officer", sub: "Connect with your district", icon: <UserAddOutlined />, color: "#d66a82" }
];

const schemes = [
    { title: "MIDH / NHM", desc: "Mission for Integrated Development of Horticulture", image: photos.fruit, tag: "Central scheme" },
    { title: "Protected Cultivation", desc: "Support for greenhouse and shade-net cultivation", image: photos.greenhouse, tag: "Infrastructure" },
    { title: "Area Expansion", desc: "Encouraging cultivation of high-value crops", image: photos.plantation, tag: "Crop development" },
    { title: "Post Harvest Management", desc: "Storage, handling and value addition facilities", image: photos.nursery, tag: "Infrastructure" },
    { title: "Micro Irrigation", desc: "Efficient water use through drip and sprinkler", image: photos.irrigation, tag: "Water management" },
    { title: "Nursery Development", desc: "Quality planting material and nursery support", image: photos.nursery, tag: "Planting material" }
];

const crops = [
    { title: "Fruits", image: photos.fruit, color: "#438b55", count: "Explore fruit crops" },
    { title: "Vegetables", image: photos.vegetable, color: "#4d9a68", count: "Explore vegetable crops" },
    { title: "Flowers", image: photos.flower, color: "#c77b3e", count: "Explore flower crops" },
    { title: "Plantation Crops", image: photos.plantation, color: "#668e4e", count: "Explore plantation crops" }
];

const districts = ["Chennai", "Coimbatore", "Madurai", "Salem", "Thanjavur", "Tiruchirappalli", "Tirunelveli", "Erode", "Dindigul", "Nilgiris"];

const news = [
    { date: "12 OCT 2026", type: "Announcement", title: "Applications invited for horticulture development schemes", desc: "Farmers can review eligibility and submit applications through the department." },
    { date: "08 OCT 2026", type: "Department Update", title: "District-level farmer training programmes announced", desc: "Training sessions will cover crop planning, water management and modern practices." },
    { date: "02 OCT 2026", type: "Guideline", title: "Updated guidelines for micro irrigation assistance", desc: "Read the latest instructions and required documents before applying." }
];

const events = [
    { date: "18", month: "OCT", title: "Horticulture Farmer Awareness Camp", place: "Coimbatore District" },
    { date: "24", month: "OCT", title: "Modern Protected Cultivation Workshop", place: "Madurai District" },
    { date: "02", month: "NOV", title: "Fruit Crop Development Training", place: "Salem District" }
];

function SectionHeading({ eyebrow, title, link, onLink }) {
    return <div className="section-heading">
        <div><Text className="eyebrow">{eyebrow}</Text><Title level={2}>{title}</Title></div>
        {link && <Button type="link" onClick={onLink}>{link} <ArrowRightOutlined /></Button>}
    </div>;
}

function SiteHeader({ onOpenService, selectedTheme, setSelectedTheme }) {
    const [mobileOpen, setMobileOpen] = useState(false);
    const navItems = [
        { key: "home", label: "Home" },
        { key: "about", label: "About Us" },
        { key: "schemes", label: "Schemes" },
        { key: "crops", label: "Crops" },
        { key: "services", label: "Services" },
        { key: "announcements", label: "Announcements" },
        { key: "contact", label: "Contact Us" }
    ];
    const go = (key) => {
        document.getElementById(key)?.scrollIntoView({ behavior: "smooth" });
        setMobileOpen(false);
    };

    const navigate = useNavigate();

    const handleThemeChange = (value) => {
        setSelectedTheme(value);

        if (value === "theme4") {
            navigate("/model2");
        } else if (value === "theme5") {
            navigate("/model3");
        }
    };

    return <>
        <div className="top-strip">
            <div className="container top-inner">
                <span>Government of Tamil Nadu</span>
                <Space size={18}><a href="#main">Skip to main content</a><span>Screen Reader Access</span><span className="font-tools">A− &nbsp; A &nbsp; A+</span><span>English ▾</span>
                    <Select
                        value={selectedTheme}
                        onChange={handleThemeChange}
                        style={{ width: 135 }}
                        options={[
                            { label: "Theme 1", value: "theme1" },
                            { label: "Theme 2", value: "theme2" },
                            { label: "Theme 3", value: "theme3" },
                            { label: "Theme 4", value: "theme4" },
                            { label: "Theme 5", value: "theme5" }
                        ]}
                    />
                </Space>
            </div>

        </div>

        <div className="brand-row container">
            <div className="brand">
                <div className="seal"><EnvironmentFilled /></div>
                <div><Text className="brand-kicker">GOVERNMENT OF TAMIL NADU</Text><Title level={4}>Department of Horticulture<br />and Plantation Crops</Title></div>
            </div>
            <div className="brand-right">
                <div className="digital-india"><span className="digital-mark">தமிழ்நாடு</span><Text>Digital services for every farmer</Text></div>
                <Input className="site-search" placeholder="Search the portal..." suffix={<SearchOutlined />} onPressEnter={(e) => message.info(`Search: ${e.target.value}`)} />
            </div>
        </div>
        <Header className="nav-shell">
            <div className="container nav-inner">
                <div className="mobile-brand">HORTICULTURE <span>TN</span></div>
                <nav className={mobileOpen ? "main-nav open" : "main-nav"}>
                    {navItems.map(item => <button key={item.key} onClick={() => go(item.key)}>{item.label}</button>)}
                </nav>
                <Button className="mobile-toggle" icon={mobileOpen ? <CloseOutlined /> : <MenuOutlined />} onClick={() => setMobileOpen(!mobileOpen)} />
                <Button className="farmer-login" onClick={() => onOpenService("Farmer Login")}>Farmer Login <ArrowRightOutlined /></Button>
            </div>
        </Header>
    </>;
}

function Hero() {
    return <section className="hero" id="home" style={{ backgroundImage: `linear-gradient(90deg, rgba(4,39,29,.88) 0%, rgba(4,39,29,.62) 48%, rgba(4,39,29,.08) 100%), url(${photos.hero})` }}>
        <div className="container hero-content">
            <Tag className="hero-tag">GROW • NOURISH • PROSPER</Tag>
            <Title>Growing Horticulture.<br /><span>Empowering Farmers.</span></Title>
            <p>Better crops. Higher incomes. A greener tomorrow.<br />Discover opportunities, services and support for Tamil Nadu farmers.</p>
            <Space wrap size={12}>
                <Button className="hero-primary" size="large" onClick={() => document.getElementById("schemes")?.scrollIntoView({ behavior: "smooth" })}>Explore Schemes <ArrowRightOutlined /></Button>
                <Button className="hero-secondary" size="large" onClick={() => document.getElementById("crops")?.scrollIntoView({ behavior: "smooth" })}>Explore Crops <ArrowRightOutlined /></Button>
            </Space>
            <div className="hero-note"><span className="live-dot" /> Supporting farmers across all 38 districts</div>
        </div>
        <div className="hero-index"><span>01</span><i /> 03</div>
    </section>;
}

function QuickServices({ onOpenService }) {
    return <section className="container quick-wrap" id="services">
        <div className="quick-title"><div><Text className="eyebrow">HOW CAN WE HELP?</Text><Title level={3}>Quick Services</Title></div><Text type="secondary">Everything you need, in one place.</Text></div>
        <Row gutter={[12, 12]}>
            {quickServices.map((service, i) => <Col xs={12} sm={8} lg={4} key={service.title}>
                <button className="service-card" onClick={() => onOpenService(service.title)}>
                    <span className="service-icon" style={{ background: `${service.color}18`, color: service.color }}>{service.icon}</span>
                    <strong>{service.title}</strong><small>{service.sub}</small>
                    <span className="service-arrow"><ArrowRightOutlined /></span>
                </button>
            </Col>)}
        </Row>
    </section>;
}

function AtGlance() {
    const stats = [
        { label: "Area Covered", value: "8.2", suffix: "Lakh Ha", icon: <EnvironmentOutlined />, color: "#76a84d" },
        { label: "Farmers Benefited", value: "12.5", suffix: "Lakh+", icon: <TeamOutlined />, color: "#329c8b" },
        { label: "Districts Covered", value: "38", suffix: "Districts", icon: <CompassOutlined />, color: "#6292bd" },
        { label: "Schemes Implemented", value: "24", suffix: "Schemes", icon: <FileTextOutlined />, color: "#9275c4" },
        { label: "Horticulture Nurseries", value: "450", suffix: "Nurseries", icon: <ExperimentOutlined />, color: "#609c74" }
    ];
    return <section className="container glance-section">
        <SectionHeading eyebrow="OUR IMPACT" title="Horticulture at a Glance" link="View details" onLink={() => message.info("Detailed department statistics can be connected here.")} />
        <Row gutter={[12, 12]}>
            {stats.map(s => <Col xs={12} sm={8} lg={24 / 5} key={s.label}>
                <Card className="stat-card" bordered={false}>
                    <span className="stat-icon" style={{ color: s.color, background: `${s.color}17` }}>{s.icon}</span>
                    <Text className="stat-label">{s.label}</Text>
                    <div className="stat-value">{s.value}<small>{s.suffix}</small></div>
                    <span className="stat-foot"><CheckCircleFilled /> Department overview</span>
                </Card>
            </Col>)}
        </Row>
    </section>;
}

function Schemes() {
    return <section className="section-soft" id="schemes"><div className="container section-pad">
        <SectionHeading eyebrow="SUPPORT & OPPORTUNITIES" title="Schemes & Guidelines" link="View all schemes" onLink={() => message.info("Connect this link to your schemes listing page.")} />
        <Row gutter={[18, 18]}>
            {schemes.map((s, i) => <Col xs={24} sm={12} lg={8} key={s.title}>
                <Card className="scheme-card" cover={<div className="scheme-image"><img src={s.image} alt={s.title} /><span className="scheme-number">0{i + 1}</span></div>} bordered={false}>
                    <Tag>{s.tag}</Tag><Title level={4}>{s.title}</Title><Paragraph>{s.desc}</Paragraph>
                    <a href="#contact" onClick={(e) => { e.preventDefault(); message.info(`More information about ${s.title} will be available here.`); }}>Know more <ArrowRightOutlined /></a>
                </Card>
            </Col>)}
        </Row>
    </div></section>;
}

function CropExplorer() {
    return <section className="container section-pad crop-section" id="crops">
        <SectionHeading eyebrow="DISCOVER & LEARN" title="Explore Horticulture by Crop" link="Explore all crops" onLink={() => message.info("Connect to crop directory.")} />
        <Row gutter={[16, 16]}>
            {crops.map(c => <Col xs={12} md={6} key={c.title}>
                <button className="crop-card" onClick={() => message.info(`Opening ${c.title} crop information.`)}>
                    <img src={c.image} alt={c.title} /><span className="crop-overlay" />
                    <span className="crop-info"><strong>{c.title}</strong><small>{c.count}</small></span><span className="crop-go"><ArrowRightOutlined /></span>
                </button>
            </Col>)}
        </Row>
    </section>;
}

function AreaCoverage() {
    const [district, setDistrict] = useState("Coimbatore");
    return <section className="area-section" id="about">
        <div className="container area-grid">
            <div className="area-copy"><Text className="eyebrow">ROOTED IN EVERY DISTRICT</Text><Title level={2}>Area Coverage</Title>
                <Paragraph>Horticulture is being promoted across Tamil Nadu to improve farm productivity, encourage crop diversification and create sustainable livelihoods.</Paragraph>
                <Button className="area-button" onClick={() => message.info("District-wise coverage page can be connected here.")}>View District-wise Details <ArrowRightOutlined /></Button>
                <div className="area-mini-stats"><div><strong>38</strong><span>Districts</span></div><div><strong>பல</strong><span>Crop varieties</span></div><div><strong>∞</strong><span>Growing potential</span></div></div>
            </div>
            <div className="map-panel">
                <div className="map-art"><div className="map-glow" /><svg viewBox="0 0 260 330" role="img" aria-label="Illustrative Tamil Nadu map"><path d="M111 12 L149 25 158 50 184 67 176 94 201 119 185 143 198 170 177 193 181 217 158 238 150 269 126 310 104 287 91 256 73 234 78 207 58 187 69 157 48 133 67 106 65 82 87 61 86 35Z" fill="#7dbb78" stroke="#3c815a" strokeWidth="3" /><path d="M88 79L163 82M67 135L183 146M77 193L174 190M97 247L157 230" stroke="#d9f0d3" strokeWidth="2" strokeDasharray="4 5" /><circle cx="143" cy="123" r="6" fill="#f5b84e" /><circle cx="108" cy="187" r="6" fill="#f5b84e" /><circle cx="145" cy="244" r="6" fill="#f5b84e" /></svg><span className="map-caption">TAMIL NADU</span></div>
                <Card className="district-card" bordered={false}>
                    <Text className="eyebrow">DISTRICT LOOKUP</Text><Title level={4}>Select District</Title>
                    <Select value={district} onChange={setDistrict} options={districts.map(d => ({ value: d, label: d }))} style={{ width: "100%" }} />
                    <Divider />
                    <div className="district-detail"><span><EnvironmentOutlined /> Selected district</span><strong>{district}</strong></div>
                    <div className="district-detail"><span><TeamOutlined /> District office</span><strong>Horticulture Department</strong></div>
                    <div className="district-detail"><span><FileTextOutlined /> Schemes</span><strong>View local assistance</strong></div>
                    <Button block type="primary" onClick={() => message.success(`Showing information for ${district}`)}>View District Details <ArrowRightOutlined /></Button>
                </Card>
            </div>
        </div>
    </section>;
}

function NewsEvents() {
    return <section className="container section-pad news-section" id="announcements">
        <SectionHeading eyebrow="WHAT'S HAPPENING" title="News, Events & Updates" link="All announcements" onLink={() => message.info("Connect to announcements listing.")} />
        <Row gutter={[24, 24]}>
            <Col xs={24} lg={12}><div className="content-panel"><div className="panel-heading"><Title level={4}>Latest Announcements</Title><BellOutlined /></div>
                {news.map(n => <div className="news-item" key={n.title}><div className="news-date">{n.date}</div><div className="news-copy"><Tag>{n.type}</Tag><strong>{n.title}</strong><Text>{n.desc}</Text></div><ArrowRightOutlined className="news-arrow" /></div>)}
            </div></Col>
            <Col xs={24} lg={6}><div className="content-panel events-panel"><div className="panel-heading"><Title level={4}>Upcoming Events</Title><CalendarOutlined /></div>
                {events.map(e => <div className="event-item" key={e.title}><div className="event-date"><strong>{e.date}</strong><span>{e.month}</span></div><div><strong>{e.title}</strong><small>{e.place}</small></div></div>)}
            </div></Col>
            <Col xs={24} lg={6}><div className="success-panel"><div className="success-image" style={{ backgroundImage: `url(${photos.farmer})` }} /><Tag color="green">FARMER STORY</Tag><Title level={4}>Growing success, one farm at a time.</Title><Paragraph>Discover how farmers are adopting modern horticulture practices to build resilient livelihoods.</Paragraph><a href="#contact">Read success stories <ArrowRightOutlined /></a></div></Col>
        </Row>
    </section>;
}

function Downloads() {
    const items = ["MIDH Operational Guidelines", "Micro Irrigation Guidelines", "Protected Cultivation Handbook", "Farmer Application Form"];
    return <section className="downloads-band"><div className="container downloads-inner"><div><Text className="eyebrow">RESOURCES & DOCUMENTS</Text><Title level={3}>Downloads & Guidelines</Title><Text>Important documents, forms and department publications.</Text></div><div className="download-links">{items.map(i => <button key={i} onClick={() => message.info(`Add the official PDF URL for: ${i}`)}><FileTextOutlined /><span>{i}</span><DownloadOutlined /></button>)}</div></div></section>;
}

function SiteFooter() {
    return <Footer className="site-footer" id="contact"><div className="container">
        <Row gutter={[32, 32]}>
            <Col xs={24} md={8}><div className="footer-brand"><div className="seal footer-seal"><EnvironmentFilled /></div><div><strong>Department of Horticulture<br />and Plantation Crops</strong><small>Government of Tamil Nadu</small></div></div><p>Enabling farmers through technology, quality planting material and sustainable horticulture development.</p></Col>
            <Col xs={12} md={5}><Title level={5}>Quick Links</Title><a href="#home">Home</a><a href="#about">About Department</a><a href="#schemes">Schemes & Subsidies</a><a href="#crops">Crop Information</a><a href="#announcements">News & Events</a></Col>
            <Col xs={12} md={5}><Title level={5}>Help & Support</Title><a href="#services">Farmer Services</a><a href="#services">Application Status</a><a href="#contact">Contact Directory</a><a href="#contact">FAQs</a><a href="#contact">Feedback</a></Col>
            <Col xs={24} md={6}><Title level={5}>Contact Department</Title><p><EnvironmentOutlined /> Horticulture and Plantation Crops Department, Chennai, Tamil Nadu</p><p><PhoneOutlined /> 044-XXXX XXXX</p><p><MailOutlined /> horticulture@tn.gov.in</p><div className="socials"><FacebookFilled /><XOutlined /><InstagramFilled /><YoutubeFilled /></div></Col>
        </Row>
        <Divider />
        <div className="footer-bottom"><span>© 2026 Government of Tamil Nadu. All Rights Reserved.</span><span>Privacy Policy · Terms of Use · Accessibility</span></div>
    </div></Footer>;
}

function ServiceModal({ service, onClose }) {
    const [form] = Form.useForm();
    return <Modal open={!!service} title={<span className="modal-title">{service}</span>} onCancel={onClose} footer={null} centered destroyOnClose>
        <Paragraph type="secondary">   is a frontend demonstration. Connect the form to the department's authorized API or service before production use.</Paragraph>
        <Form form={form} layout="vertical" onFinish={(values) => { message.success("Demo form submitted. Connect the backend to process this request."); onClose(); }}>
            <Form.Item label="Registered mobile number" name="mobile" rules={[{ required: true, message: "Enter your mobile number" }, { pattern: /^[6-9]\d{9}$/, message: "Enter a valid 10-digit mobile number" }]}>
                <Input size="large" maxLength={10} placeholder="Enter 10-digit mobile number" />
            </Form.Item>
            {(service === "Check Application Status" || service === "Apply for Subsidy") && <Form.Item label="Application reference number" name="reference" rules={[{ required: true, message: "Enter your reference number" }]}><Input size="large" placeholder="Enter application reference" /></Form.Item>}
            <Button type="primary" htmlType="submit" block size="large">Continue <ArrowRightOutlined /></Button>
        </Form>
    </Modal>;
}

export default function Model1() {
    const [activeService, setActiveService] = useState(null);
    const [selectedTheme, setSelectedTheme] = useState("theme1");

    return <ConfigProvider theme={{ token: { colorPrimary: "#16734f", borderRadius: 12, fontFamily: "'DM Sans', 'Segoe UI', sans-serif", colorText: "#17372d" }, components: { Button: { controlHeight: 42 }, Card: { borderRadiusLG: 18 } } }}>
        <Layout className={`app-layout ${selectedTheme === "theme1"
            ? "theme1-design"
            : selectedTheme === "theme2"
                ? "theme2-design"
                : selectedTheme === "theme3"
                    ? "theme3-design"
                    : "theme1-design"
            }`}>

            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=Manrope:wght@400;500;600;700;800&display=swap');
        *{box-sizing:border-box}html{scroll-behavior:smooth}body{margin:0;background:#fff;color:#17372d;font-family:'DM Sans',sans-serif}.app-layout{min-height:100vh;background:#fff}.container{width:min(1180px,calc(100% - 48px));margin:0 auto}
        .top-strip{background:#073e30;color:#d6e9df;font-size:11px;letter-spacing:.04em}.top-inner{height:34px;display:flex;align-items:center;justify-content:space-between}.top-inner a{color:#d6e9df}.font-tools{color:#fff}
        .brand-row{height:88px;display:flex;align-items:center;justify-content:space-between}.brand{display:flex;align-items:center;gap:13px}.seal{width:48px;height:48px;border-radius:50%;background:#e9f3e9;color:#16734f;display:grid;place-items:center;font-size:23px;border:1px solid #d4e5d8}.brand-kicker{font-size:9px;letter-spacing:.14em;color:#688477;font-weight:700}.brand .ant-typography{margin:2px 0 0;font-size:17px;line-height:1.22;font-weight:800;color:#164331}.brand-right{display:flex;align-items:center;gap:24px}.digital-india{display:flex;flex-direction:column;align-items:flex-end;font-size:10px;color:#789087}.digital-mark{font-size:15px;color:#16734f;font-weight:800}.site-search{width:230px;border-radius:30px;background:#f5f8f5;border:1px solid #e8eee9}
        .nav-shell{height:auto;line-height:normal;padding:0;background:#0d5942;position:sticky;top:0;z-index:20;box-shadow:0 6px 20px #092e201c}.nav-inner{height:52px;display:flex;align-items:center;justify-content:space-between}.main-nav{height:100%;display:flex;align-items:center;gap:4px}.main-nav button{height:100%;border:0;background:transparent;color:#e4f2e9;padding:0 15px;font:600 12px 'DM Sans';cursor:pointer;transition:.2s}.main-nav button:hover{background:#ffffff18;color:#fff}.farmer-login{background:#d6edb9!important;color:#174a34!important;border:0!important;border-radius:8px!important;font-weight:700}.mobile-toggle,.mobile-brand{display:none}
        .hero{min-height:430px;background-position:center;background-size:cover;position:relative;color:#fff;display:flex;align-items:center}.hero-content{padding:65px 0 76px}.hero-tag{background:#ffffff18;border:1px solid #ffffff45;color:#e6f5df;border-radius:30px;padding:5px 12px;font-size:10px;letter-spacing:.13em}.hero h1{font-family:Manrope,sans-serif;color:#fff;font-size:clamp(36px,5vw,58px);line-height:1.08;letter-spacing:-.045em;margin:20px 0 14px;font-weight:800}.hero h1 span{color:#bde18d}.hero p{color:#e0eee4;font-size:14px;line-height:1.8;margin-bottom:25px}.hero-primary{background:#b8db87!important;color:#153f2e!important;border:0!important;font-weight:700}.hero-secondary{background:#ffffff15!important;border:1px solid #ffffff78!important;color:#fff!important}.hero-note{margin-top:27px;color:#d7e7dd;font-size:11px}.live-dot{display:inline-block;width:7px;height:7px;border-radius:50%;background:#a8dc76;margin-right:8px;box-shadow:0 0 0 4px #a8dc7628}.hero-index{position:absolute;right:max(24px,calc((100vw - 1180px)/2));bottom:26px;color:#d7e7dd;font-size:11px;display:flex;align-items:center;gap:10px}.hero-index span{font-size:18px;color:#fff}.hero-index i{height:1px;width:38px;background:#b7d78a}
        .quick-wrap{padding:34px 0 44px}.quick-title{display:flex;align-items:end;justify-content:space-between;margin-bottom:18px}.quick-title h3{margin:2px 0 0;font-size:22px;color:#17372d}.quick-title .ant-typography-secondary{font-size:12px}.eyebrow{font-size:10px;letter-spacing:.16em;color:#73927e;font-weight:800}.service-card{position:relative;text-align:left;width:100%;min-height:154px;padding:17px 14px;border:1px solid #e9f0eb;border-radius:15px;background:#fff;display:flex;flex-direction:column;align-items:flex-start;cursor:pointer;transition:.25s;box-shadow:0 7px 20px #163e2a08}.service-card:hover{transform:translateY(-5px);border-color:#b9d7c1;box-shadow:0 15px 30px #163e2a12}.service-icon{height:42px;width:42px;border-radius:13px;display:grid;place-items:center;font-size:20px;margin-bottom:13px}.service-card strong{font-size:12px;color:#214737;line-height:1.4}.service-card small{font-size:10px;color:#8a9b91;margin-top:5px}.service-arrow{position:absolute;right:14px;top:18px;color:#a5b7aa;font-size:12px}
        .glance-section{padding:12px 0 58px}.section-heading{display:flex;justify-content:space-between;align-items:end;margin-bottom:22px}.section-heading h2{font-size:28px;letter-spacing:-.04em;margin:3px 0 0;color:#17372d;font-family:Manrope,sans-serif}.section-heading .ant-btn{font-size:12px;font-weight:700;color:#24744f}.stat-card{height:100%;border:1px solid #edf2ee;box-shadow:0 8px 22px #173b2a07;padding:3px}.stat-card .ant-card-body{padding:18px 16px}.stat-icon{width:37px;height:37px;border-radius:12px;display:grid;place-items:center;font-size:18px;margin-bottom:14px}.stat-label{font-size:11px;color:#7a9083}.stat-value{font-size:26px;line-height:1.2;font-weight:800;color:#194633;margin-top:5px;letter-spacing:-.04em}.stat-value small{font-size:10px;letter-spacing:0;font-weight:600;color:#87998e;margin-left:5px}.stat-foot{font-size:9px;color:#91a398;display:block;margin-top:13px}.stat-foot .anticon{color:#6aab76;margin-right:5px}
        .section-soft{background:#f5f8f4}.section-pad{padding-top:58px;padding-bottom:60px}.scheme-card{height:100%;overflow:hidden;border:1px solid #e8eee8;box-shadow:0 8px 24px #183c2808;transition:.25s}.scheme-card:hover{transform:translateY(-4px);box-shadow:0 16px 30px #183c2814}.scheme-image{height:165px;position:relative;overflow:hidden}.scheme-image img{width:100%;height:100%;object-fit:cover;transition:transform .5s}.scheme-card:hover .scheme-image img{transform:scale(1.05)}.scheme-number{position:absolute;right:13px;top:13px;background:#ffffffdf;color:#1c5c40;border-radius:8px;padding:5px 8px;font-size:10px;font-weight:800}.scheme-card .ant-card-body{padding:17px 18px 20px}.scheme-card .ant-tag{border:0;background:#edf5e9;color:#56805a;font-size:9px;border-radius:20px}.scheme-card h4{font-size:16px;margin:9px 0 5px;color:#1d4433}.scheme-card p{font-size:11px;line-height:1.65;color:#829187;min-height:37px;margin-bottom:12px}.scheme-card a,.success-panel a{font-size:11px;color:#23764e;font-weight:700}
        .crop-section{padding-bottom:68px}.crop-card{height:195px;width:100%;position:relative;border:0;border-radius:17px;overflow:hidden;cursor:pointer;text-align:left;padding:0;background:#254d37}.crop-card img{height:100%;width:100%;object-fit:cover;transition:transform .5s}.crop-card:hover img{transform:scale(1.08)}.crop-overlay{position:absolute;inset:0;background:linear-gradient(0deg,#0a2d20dc 0%,#0a2d2010 75%)}.crop-info{position:absolute;left:17px;bottom:17px;color:white;display:flex;flex-direction:column}.crop-info strong{font-size:17px}.crop-info small{font-size:10px;color:#e0eee4;margin-top:3px}.crop-go{position:absolute;right:15px;bottom:17px;width:31px;height:31px;border-radius:50%;background:#ffffffed;color:#1d6848;display:grid;place-items:center}
        .area-section{background:#eaf2e8;overflow:hidden;position:relative}.area-grid{display:grid;grid-template-columns:.8fr 1.2fr;gap:50px;align-items:center;padding-top:62px;padding-bottom:62px}.area-copy h2{font-size:34px;letter-spacing:-.04em;margin:5px 0 13px;color:#17432f}.area-copy p{font-size:13px;line-height:1.9;color:#71877a;max-width:390px}.area-button{background:#1a714d;color:white;border:0;margin-top:10px;font-size:11px;font-weight:700}.area-mini-stats{display:flex;gap:30px;margin-top:32px}.area-mini-stats div{display:flex;flex-direction:column}.area-mini-stats strong{font-size:23px;color:#1d6746}.area-mini-stats span{font-size:10px;color:#829489}.map-panel{min-height:340px;position:relative;display:flex;align-items:center;justify-content:space-between;gap:10px}.map-art{width:48%;height:330px;position:relative;display:grid;place-items:center}.map-art svg{height:290px;max-width:100%;filter:drop-shadow(0 12px 15px #315c3920)}.map-glow{position:absolute;width:230px;height:230px;border-radius:50%;background:#c9e3c4;filter:blur(25px)}.map-caption{position:absolute;bottom:5px;font-size:10px;letter-spacing:.25em;color:#60876b;font-weight:800}.district-card{width:52%;max-width:310px;box-shadow:0 15px 35px #1a4d3214;border:1px solid #ffffff}.district-card .ant-card-body{padding:20px}.district-card h4{font-size:17px;margin:4px 0 13px}.district-card .ant-divider{margin:15px 0}.district-detail{display:flex;flex-direction:column;gap:4px;margin:10px 0}.district-detail span{font-size:10px;color:#84978b}.district-detail span .anticon{margin-right:6px}.district-detail strong{font-size:11px;color:#2d5741}.district-card .ant-btn{margin-top:9px;font-size:11px}
        .news-section{padding-bottom:65px}.content-panel{height:100%;background:#f8faf7;border:1px solid #edf1ed;border-radius:17px;padding:20px}.panel-heading{display:flex;justify-content:space-between;align-items:center;margin-bottom:14px;color:#7c9a83}.panel-heading h4{font-size:16px;margin:0;color:#214533}.news-item{display:grid;grid-template-columns:82px 1fr 14px;gap:12px;padding:15px 0;border-top:1px solid #e9efea;align-items:start}.news-date{font-size:9px;color:#77917f;font-weight:800;padding-top:4px}.news-copy{display:flex;flex-direction:column;align-items:flex-start;gap:5px}.news-copy .ant-tag{font-size:8px;border:0;background:#e8f2e6;color:#56835d;border-radius:20px;margin:0}.news-copy strong{font-size:11px;color:#31523f;line-height:1.5}.news-copy .ant-typography{font-size:10px;color:#8a9a90;line-height:1.5;margin:0}.news-arrow{color:#9aae9f;margin-top:5px}.events-panel .panel-heading{margin-bottom:18px}.event-item{display:flex;gap:12px;margin:16px 0;align-items:center}.event-date{width:43px;height:48px;border-radius:9px;background:#e6f0e4;display:flex;flex-direction:column;align-items:center;justify-content:center;color:#34714b;flex-shrink:0}.event-date strong{font-size:17px;line-height:1}.event-date span{font-size:8px;font-weight:800}.event-item>div:last-child{display:flex;flex-direction:column;gap:5px}.event-item>div:last-child strong{font-size:10px;line-height:1.5;color:#365640}.event-item small{font-size:9px;color:#899b90}.success-panel{border-radius:17px;overflow:hidden;background:#f4f8f2;border:1px solid #eaf0e8;padding:15px 16px 20px}.success-image{height:112px;background-position:center;background-size:cover;border-radius:11px;margin-bottom:14px}.success-panel .ant-tag{font-size:8px;border:0;border-radius:20px}.success-panel h4{font-size:16px;line-height:1.3;margin:9px 0;color:#214733}.success-panel p{font-size:10px;line-height:1.7;color:#829287}
        .downloads-band{background:#e5eee3;border-top:1px solid #dce8dc}.downloads-inner{display:grid;grid-template-columns:.75fr 1.25fr;gap:45px;align-items:center;padding-top:34px;padding-bottom:34px}.downloads-inner h3{font-size:23px;margin:4px 0;color:#1a4934}.downloads-inner>div:first-child>.ant-typography:last-child{font-size:11px;color:#829488}.download-links{display:grid;grid-template-columns:1fr 1fr;gap:8px}.download-links button{border:1px solid #d7e4d7;background:#f8fbf7;border-radius:9px;padding:12px;display:flex;align-items:center;gap:9px;text-align:left;color:#50705a;cursor:pointer;font-size:10px}.download-links button span{flex:1}.download-links button:hover{border-color:#82b28b;color:#1d704b}
        .site-footer{background:#0b4938;color:#d6e6dc;padding:48px 0 18px}.site-footer h5{color:#fff;font-size:13px;margin:3px 0 18px}.site-footer p{font-size:10px;line-height:1.8;color:#b6cfc0;margin-top:14px}.site-footer a{display:block;color:#bdd3c5;font-size:10px;margin:11px 0}.site-footer a:hover{color:#d4ed9c}.footer-brand{display:flex;align-items:center;gap:12px}.footer-brand strong{font-size:13px;line-height:1.4;color:#fff}.footer-brand small{display:block;font-size:9px;color:#a8c8b5;margin-top:5px}.footer-seal{background:#dcebd9;border:0}.socials{display:flex;gap:15px;font-size:17px;color:#c2ddb9;margin-top:18px}.site-footer .ant-divider{border-color:#ffffff24;margin:30px 0 16px}.footer-bottom{display:flex;justify-content:space-between;color:#91b2a0;font-size:9px}.modal-title{font-weight:800;color:#174b35}
        @media(max-width:900px){.main-nav button{padding:0 8px;font-size:10px}.brand-right .digital-india{display:none}.area-grid{gap:20px}.map-panel{gap:0}.map-art{width:42%}.district-card{width:58%}.stat-card .ant-card-body{padding:14px 11px}}
        @media(max-width:680px){.container{width:calc(100% - 30px)}.top-inner{height:30px}.top-inner>span{font-size:9px}.top-inner .ant-space{gap:8px!important;font-size:9px}.top-inner .ant-space span:nth-child(2),.font-tools{display:none}.brand-row{height:72px}.seal{width:40px;height:40px;font-size:19px}.brand-kicker{font-size:7px}.brand .ant-typography{font-size:13px}.brand-right{gap:0}.site-search{width:38px;border:0;background:transparent}.site-search input{display:none}.nav-inner{height:50px}.mobile-brand{display:block;color:white;font-size:12px;font-weight:800;letter-spacing:.08em}.mobile-brand span{color:#c8e99a}.mobile-toggle{display:inline-flex;background:transparent!important;border:0!important;color:white!important}.farmer-login{font-size:10px;height:34px!important}.main-nav{display:none;position:absolute;top:50px;left:0;right:0;background:#0d5942;padding:10px 15px 16px;box-shadow:0 12px 20px #0002;flex-direction:column;align-items:stretch;height:auto}.main-nav.open{display:flex}.main-nav button{height:40px;text-align:left;font-size:12px;border-radius:7px}.hero{min-height:390px;background-position:58% center}.hero-content{padding:55px 0}.hero h1{font-size:39px}.hero p{font-size:12px}.hero-index{right:18px;bottom:16px}.quick-wrap{padding:27px 0 35px}.quick-title{align-items:start;flex-direction:column;gap:3px}.quick-title h3{font-size:20px}.quick-title .ant-typography-secondary{font-size:10px}.service-card{min-height:142px;padding:13px 11px}.service-icon{width:36px;height:36px;font-size:17px;margin-bottom:10px}.service-card strong{font-size:10px}.service-card small{font-size:9px}.section-heading h2{font-size:23px}.section-heading .ant-btn{font-size:10px;padding-inline:4px}.glance-section{padding-bottom:40px}.stat-card .ant-card-body{padding:12px 10px}.stat-icon{width:31px;height:31px;font-size:15px;margin-bottom:9px}.stat-label{font-size:9px}.stat-value{font-size:21px}.stat-value small{font-size:8px;display:block;margin:2px 0 0}.stat-foot{font-size:8px;margin-top:8px}.section-pad{padding-top:40px;padding-bottom:42px}.scheme-image{height:140px}.scheme-card .ant-card-body{padding:13px}.scheme-card h4{font-size:14px}.scheme-card p{font-size:10px;min-height:0}.crop-card{height:155px}.crop-info{left:11px;bottom:12px}.crop-info strong{font-size:13px}.crop-info small{font-size:8px}.crop-go{right:10px;bottom:11px;width:26px;height:26px}.area-grid{grid-template-columns:1fr;padding-top:40px;padding-bottom:40px}.area-copy h2{font-size:29px}.area-copy p{font-size:11px}.area-mini-stats{margin-top:22px}.map-panel{min-height:300px}.map-art{height:290px;width:45%}.map-art svg{height:250px}.district-card{width:55%;padding:0}.district-card .ant-card-body{padding:13px}.district-card h4{font-size:14px}.district-detail strong{font-size:9px}.district-card .ant-btn{font-size:9px;padding-inline:5px}.news-section{padding-bottom:42px}.content-panel{padding:15px}.news-item{grid-template-columns:68px 1fr 12px;gap:8px}.news-date{font-size:8px}.news-copy strong{font-size:10px}.downloads-inner{grid-template-columns:1fr;gap:15px;padding-top:28px;padding-bottom:28px}.downloads-inner h3{font-size:20px}.download-links{gap:6px}.download-links button{font-size:9px;padding:10px 8px}.site-footer{padding-top:35px}.footer-bottom{gap:10px;flex-direction:column}.site-footer p{font-size:10px}}
        @media(prefers-reduced-motion:reduce){*,*::before,*::after{scroll-behavior:auto!important;transition:none!important}}
      /* Theme Selector */

.theme-selector {
    min-width: 135px;
}

.theme-selector .ant-select-selector {
    border-radius: 6px !important;
    border-color: #dce5df !important;
}

.brand-right {
    display: flex;
    align-items: center;
    gap: 15px;
    flex-wrap: wrap;
}

@media (max-width: 768px) {
    .brand-right {
        width: 100%;
        justify-content: space-between;
    }

    .brand-right .ant-select {
        min-width: 115px;
    }

    .brand-right .site-search {
        width: 100%;
    }
}
      `}</style>

            <style>{`
      
/* =========================================
   THEME 2 - MODERN GOVERNMENT PORTAL
   Navy Blue + Teal + Gold
========================================= */

.theme2-design {
  background: #ffffff;
  color: #243746;
}

/* GLOBAL */

.theme2-design .container {
  width: min(1200px, calc(100% - 48px));
  margin: 0 auto;
}

.theme2-design .section-heading h2,
.theme2-design .quick-title h3 {
  color: #12344a;
  font-weight: 800;
  letter-spacing: -0.5px;
}

.theme2-design .eyebrow {
  color: #16766f;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 2px;
}

/* TOP STRIP */

.theme2-design .top-strip {
  background: #102c40;
  color: #e7eff3;
}

.theme2-design .top-inner {
  min-height: 35px;
}

.theme2-design .top-inner a {
  color: #e7eff3;
}

/* BRAND HEADER */

.theme2-design .brand-row {
  min-height: 94px;
}

.theme2-design .seal {
  background: #eaf2f4;
  border: 1px solid #d5e1e5;
  color: #16766f;
}

.theme2-design .brand-kicker {
  color: #718995;
}

.theme2-design .brand .ant-typography {
  color: #12344a;
  font-weight: 800;
}

.theme2-design .site-search {
  background: #f4f7f8;
  border: 1px solid #dce5e9;
  border-radius: 5px;
}

.theme2-design .site-search:focus-within {
  border-color: #16766f;
}

/* NAVIGATION */

.theme2-design .nav-shell {
  background: #123d53;
  box-shadow: 0 4px 15px rgba(12, 40, 55, 0.13);
}

.theme2-design .main-nav button {
  color: #ffffff;
  transition: all 0.25s ease;
}

.theme2-design .main-nav button:hover {
  background: #16766f;
  color: #ffffff;
}

.theme2-design .farmer-login {
  background: #d9b65d !important;
  color: #18384a !important;
  border-radius: 4px !important;
  font-weight: 800;
}

.theme2-design .farmer-login:hover {
  background: #e7c978 !important;
}

/* HERO SECTION */

.theme2-design .hero {
  min-height: 470px;
  background-position: center;
  position: relative;
}

.theme2-design .hero-tag {
  background: rgba(22, 118, 111, 0.8);
  border: 1px solid rgba(255,255,255,0.3);
  color: #ffffff;
  border-radius: 3px;
  padding: 7px 14px;
  letter-spacing: 1.5px;
}

.theme2-design .hero h1 {
  color: #ffffff;
  font-size: clamp(38px, 5vw, 60px);
  font-weight: 800;
  line-height: 1.12;
  letter-spacing: -1.5px;
}

.theme2-design .hero h1 span {
  color: #e5c36d;
}

.theme2-design .hero p {
  color: #e2ebee;
  font-size: 14px;
  line-height: 1.9;
}

.theme2-design .hero-primary {
  background: #d9b65d !important;
  color: #18384a !important;
  border: none !important;
  border-radius: 4px;
  font-weight: 800;
}

.theme2-design .hero-primary:hover {
  background: #e9cb7c !important;
}

.theme2-design .hero-secondary {
  background: transparent !important;
  color: #ffffff !important;
  border: 1px solid #ffffff !important;
  border-radius: 4px;
}

.theme2-design .hero-note {
  color: #d7e4e8;
}

.theme2-design .live-dot {
  background: #d9b65d;
}

/* QUICK SERVICES */

.theme2-design .quick-wrap {
  padding-top: 48px;
  padding-bottom: 50px;
}

.theme2-design .service-card {
  min-height: 155px;
  background: #ffffff;
  border: 1px solid #e1e9ec;
  border-radius: 5px;
  box-shadow: 0 5px 18px rgba(18, 52, 74, 0.05);
  transition: all 0.25s ease;
}

.theme2-design .service-card:hover {
  transform: translateY(-5px);
  border-color: #16766f;
  box-shadow: 0 12px 25px rgba(18, 52, 74, 0.1);
}

.theme2-design .service-icon {
  border-radius: 5px;
}

.theme2-design .service-card strong {
  color: #123d53;
  font-size: 12px;
}

.theme2-design .service-card small {
  color: #778b95;
}

.theme2-design .service-arrow {
  color: #16766f;
}

/* STATISTICS */

.theme2-design .glance-section {
  padding-bottom: 60px;
}

.theme2-design .stat-card {
  background: #f7f9fa;
  border: 1px solid #e4ebee;
  border-radius: 5px;
  box-shadow: none;
  transition: all 0.25s ease;
}

.theme2-design .stat-card:hover {
  background: #ffffff;
  border-color: #16766f;
  transform: translateY(-3px);
}

.theme2-design .stat-icon {
  border-radius: 5px;
  background: #e4f0ef !important;
  color: #16766f !important;
}

.theme2-design .stat-label {
  color: #758a94;
}

.theme2-design .stat-value {
  color: #123d53;
  font-weight: 800;
}

.theme2-design .stat-value small {
  color: #71858e;
}

/* SCHEMES */

.theme2-design .section-soft {
  background: #f3f6f7;
}

.theme2-design .scheme-card {
  border-radius: 5px;
  border: 1px solid #e1e8eb;
  box-shadow: 0 5px 18px rgba(18, 52, 74, 0.04);
  transition: all 0.25s ease;
}

.theme2-design .scheme-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 25px rgba(18, 52, 74, 0.1);
}

.theme2-design .scheme-image {
  height: 175px;
}

.theme2-design .scheme-number {
  border-radius: 3px;
  background: #123d53;
  color: #ffffff;
}

.theme2-design .scheme-card .ant-tag {
  background: #e4f0ef;
  color: #16766f;
  border-radius: 3px;
}

.theme2-design .scheme-card h4 {
  color: #123d53;
  font-size: 16px;
}

.theme2-design .scheme-card p {
  color: #778b95;
  line-height: 1.8;
}

.theme2-design .scheme-card a {
  color: #16766f;
  font-weight: 800;
}

/* CROP EXPLORER */

.theme2-design .crop-card {
  border-radius: 5px;
  height: 205px;
}

.theme2-design .crop-overlay {
  background: linear-gradient(
    0deg,
    rgba(10, 39, 54, 0.92),
    rgba(10, 39, 54, 0.05)
  );
}

.theme2-design .crop-info strong {
  color: #ffffff;
}

.theme2-design .crop-go {
  background: #d9b65d;
  color: #12344a;
  border-radius: 4px;
}

/* AREA COVERAGE */

.theme2-design .area-section {
  background: #eaf0f2;
}

.theme2-design .area-copy h2 {
  color: #123d53;
}

.theme2-design .area-copy p {
  color: #657d89;
}

.theme2-design .area-button {
  background: #16766f;
  color: #ffffff;
  border-radius: 4px;
}

.theme2-design .area-button:hover {
  background: #105b56 !important;
  color: #ffffff !important;
}

.theme2-design .area-mini-stats strong {
  color: #16766f;
}

.theme2-design .map-glow {
  background: #d3e4e6;
}

.theme2-design .district-card {
  border-radius: 5px;
  box-shadow: 0 8px 25px rgba(18, 52, 74, 0.08);
}

.theme2-design .district-card h4 {
  color: #123d53;
}

.theme2-design .district-detail strong {
  color: #123d53;
}

.theme2-design .district-card .ant-btn {
  background: #16766f;
  border-radius: 4px;
}

/* NEWS AND EVENTS */

.theme2-design .content-panel {
  background: #f7f9fa;
  border: 1px solid #e4ebee;
  border-radius: 5px;
}

.theme2-design .panel-heading h4 {
  color: #123d53;
}

.theme2-design .news-item {
  border-color: #e1e8eb;
}

.theme2-design .news-date {
  color: #16766f;
}

.theme2-design .news-copy .ant-tag {
  background: #e4f0ef;
  color: #16766f;
  border-radius: 3px;
}

.theme2-design .news-copy strong {
  color: #23495b;
}

.theme2-design .news-arrow {
  color: #16766f;
}

.theme2-design .event-date {
  background: #e4f0ef;
  color: #16766f;
  border-radius: 4px;
}

.theme2-design .success-panel {
  background: #f3f6f7;
  border: 1px solid #e1e8eb;
  border-radius: 5px;
}

.theme2-design .success-panel h4 {
  color: #123d53;
}

.theme2-design .success-panel a {
  color: #16766f;
}

/* DOWNLOADS */

.theme2-design .downloads-band {
  background: #e6edef;
  border-color: #dce5e8;
}

.theme2-design .downloads-inner h3 {
  color: #123d53;
}

.theme2-design .download-links button {
  background: #ffffff;
  border: 1px solid #d8e3e7;
  color: #315a69;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.theme2-design .download-links button:hover {
  border-color: #16766f;
  color: #16766f;
  background: #f5fbfa;
}

/* FOOTER */

.theme2-design .site-footer {
  background: #102c40;
  color: #ffffff;
}

.theme2-design .site-footer h5 {
  color: #e5c36d;
}

.theme2-design .site-footer p {
  color: #c2d1d7;
}

.theme2-design .site-footer a {
  color: #d2e0e4;
}

.theme2-design .site-footer a:hover {
  color: #e5c36d;
}

.theme2-design .footer-seal {
  background: #e5eef0;
  color: #16766f;
}

.theme2-design .socials {
  color: #e5c36d;
}

.theme2-design .footer-bottom {
  color: #a9bec6;
}

/* MODAL */

.theme2-design .modal-title {
  color: #123d53;
}

/* RESPONSIVE */

@media (max-width: 680px) {
  .theme2-design .hero {
    min-height: 390px;
  }

  .theme2-design .hero h1 {
    font-size: 37px;
  }

  .theme2-design .service-card {
    min-height: 140px;
  }

  .theme2-design .section-heading h2 {
    font-size: 22px;
  }

  .theme2-design .scheme-image {
    height: 140px;
  }

  .theme2-design .crop-card {
    height: 155px;
  }

  .theme2-design .area-grid {
    gap: 15px;
  }

  .theme2-design .downloads-inner {
    gap: 15px;
  }
}

      `}</style>


            <style>{`

/* =========================================
   THEME 3 - PREMIUM AGRICULTURE PORTAL
   Forest Green + Leaf Green + Orange
========================================= */

.theme3-design {
  background: #ffffff;
  color: #34483b;
  font-family: "Inter", sans-serif;
}

/* GLOBAL */

.theme3-design .container {
  width: min(1200px, calc(100% - 48px));
  margin: 0 auto;
}

.theme3-design .section-heading h2,
.theme3-design .quick-title h3 {
  color: #174c32;
  font-weight: 800;
  letter-spacing: -0.7px;
}

.theme3-design .eyebrow {
  color: #e18a36;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 2px;
}

/* TOP STRIP */

.theme3-design .top-strip {
  background: #123d29;
  color: #e8f1e9;
}

.theme3-design .top-inner {
  min-height: 36px;
}

.theme3-design .top-inner a {
  color: #e8f1e9;
}

/* BRAND HEADER */

.theme3-design .brand-row {
  min-height: 100px;
  background: #ffffff;
}

.theme3-design .seal {
  background: #e8f2e9;
  border: 1px solid #d3e5d5;
  color: #27824a;
  border-radius: 50%;
}

.theme3-design .brand-kicker {
  color: #7d9784;
  font-weight: 700;
}

.theme3-design .brand .ant-typography {
  color: #174c32;
  font-weight: 800;
}

.theme3-design .site-search {
  background: #f5f8f4;
  border: 1px solid #dce8dc;
  border-radius: 30px;
}

.theme3-design .site-search:focus-within {
  border-color: #3d985b;
  box-shadow: 0 0 0 3px rgba(61, 152, 91, 0.1);
}

/* NAVIGATION */

.theme3-design .nav-shell {
  background: #1b603b;
  box-shadow: 0 5px 18px rgba(23, 76, 50, 0.12);
}

.theme3-design .main-nav button {
  color: #ffffff;
  transition: all 0.25s ease;
  border-radius: 5px;
}

.theme3-design .main-nav button:hover {
  background: #338a50;
  color: #ffffff;
}

.theme3-design .farmer-login {
  background: #f09a42 !important;
  color: #ffffff !important;
  border-radius: 25px !important;
  font-weight: 800;
  border: none !important;
}

.theme3-design .farmer-login:hover {
  background: #dc7e27 !important;
}

/* HERO SECTION */

.theme3-design .hero {
  min-height: 490px;
  background-position: center;
  position: relative;
}

.theme3-design .hero-tag {
  background: #e18a36;
  color: #ffffff;
  border: none;
  border-radius: 30px;
  padding: 8px 16px;
  letter-spacing: 1px;
}

.theme3-design .hero h1 {
  color: #ffffff;
  font-size: clamp(38px, 5vw, 62px);
  font-weight: 800;
  line-height: 1.12;
  letter-spacing: -1.5px;
}

.theme3-design .hero h1 span {
  color: #a9e59a;
}

.theme3-design .hero p {
  color: #e9f3e9;
  font-size: 14px;
  line-height: 1.9;
}

.theme3-design .hero-primary {
  background: #f09a42 !important;
  color: #ffffff !important;
  border: none !important;
  border-radius: 30px;
  font-weight: 800;
}

.theme3-design .hero-primary:hover {
  background: #dc7e27 !important;
}

.theme3-design .hero-secondary {
  background: rgba(255,255,255,0.08) !important;
  color: #ffffff !important;
  border: 1px solid rgba(255,255,255,0.7) !important;
  border-radius: 30px;
}

.theme3-design .hero-note {
  color: #d8ead8;
}

.theme3-design .live-dot {
  background: #a9e59a;
}

/* QUICK SERVICES */

.theme3-design .quick-wrap {
  padding-top: 55px;
  padding-bottom: 55px;
  background: #ffffff;
}

.theme3-design .service-card {
  min-height: 160px;
  background: #ffffff;
  border: 1px solid #e4eee4;
  border-radius: 14px;
  box-shadow: 0 7px 24px rgba(30, 95, 55, 0.06);
  transition: all 0.3s ease;
}

.theme3-design .service-card:hover {
  transform: translateY(-7px);
  border-color: #73b97c;
  box-shadow: 0 15px 30px rgba(30, 95, 55, 0.13);
}

.theme3-design .service-icon {
  border-radius: 12px;
  background: #e8f4e9 !important;
  color: #27824a !important;
}

.theme3-design .service-card strong {
  color: #174c32;
  font-size: 13px;
}

.theme3-design .service-card small {
  color: #829487;
}

.theme3-design .service-arrow {
  color: #e18a36;
}

/* STATISTICS */

.theme3-design .glance-section {
  padding-top: 25px;
  padding-bottom: 65px;
  background: #f5f9f3;
}

.theme3-design .stat-card {
  background: #ffffff;
  border: 1px solid #e4eee4;
  border-radius: 14px;
  box-shadow: 0 5px 18px rgba(30, 95, 55, 0.04);
  transition: all 0.3s ease;
}

.theme3-design .stat-card:hover {
  border-color: #73b97c;
  transform: translateY(-5px);
  box-shadow: 0 12px 25px rgba(30, 95, 55, 0.1);
}

.theme3-design .stat-icon {
  border-radius: 12px;
  background: #e8f4e9 !important;
  color: #27824a !important;
}

.theme3-design .stat-label {
  color: #829487;
}

.theme3-design .stat-value {
  color: #174c32;
  font-weight: 800;
}

.theme3-design .stat-value small {
  color: #829487;
}

/* SCHEMES */

.theme3-design .section-soft {
  background: #f7faf5;
}

.theme3-design .scheme-card {
  border-radius: 14px;
  border: 1px solid #e4eee4;
  overflow: hidden;
  box-shadow: 0 6px 20px rgba(30, 95, 55, 0.05);
  transition: all 0.3s ease;
}

.theme3-design .scheme-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 15px 30px rgba(30, 95, 55, 0.12);
}

.theme3-design .scheme-image {
  height: 180px;
}

.theme3-design .scheme-number {
  border-radius: 30px;
  background: #1b603b;
  color: #ffffff;
}

.theme3-design .scheme-card .ant-tag {
  background: #e8f4e9;
  color: #27824a;
  border-radius: 20px;
  border: none;
}

.theme3-design .scheme-card h4 {
  color: #174c32;
  font-size: 17px;
  font-weight: 800;
}

.theme3-design .scheme-card p {
  color: #7a8d7d;
  line-height: 1.8;
}

.theme3-design .scheme-card a {
  color: #27824a;
  font-weight: 800;
}

/* CROP EXPLORER */

.theme3-design .crop-card {
  border-radius: 14px;
  height: 215px;
  overflow: hidden;
}

.theme3-design .crop-overlay {
  background: linear-gradient(
    0deg,
    rgba(13, 55, 31, 0.94),
    rgba(13, 55, 31, 0.02)
  );
}

.theme3-design .crop-info strong {
  color: #ffffff;
}

.theme3-design .crop-go {
  background: #f09a42;
  color: #ffffff;
  border-radius: 50%;
}

/* AREA COVERAGE */

.theme3-design .area-section {
  background: #edf5e9;
}

.theme3-design .area-copy h2 {
  color: #174c32;
  font-weight: 800;
}

.theme3-design .area-copy p {
  color: #6e8672;
}

.theme3-design .area-button {
  background: #27824a;
  color: #ffffff;
  border-radius: 30px;
  border: none;
}

.theme3-design .area-button:hover {
  background: #185f35 !important;
  color: #ffffff !important;
}

.theme3-design .area-mini-stats strong {
  color: #27824a;
}

.theme3-design .map-glow {
  background: #dcebd8;
}

.theme3-design .district-card {
  border-radius: 14px;
  box-shadow: 0 10px 28px rgba(30, 95, 55, 0.1);
}

.theme3-design .district-card h4 {
  color: #174c32;
}

.theme3-design .district-detail strong {
  color: #174c32;
}

.theme3-design .district-card .ant-btn {
  background: #27824a;
  border-radius: 25px;
}

/* NEWS AND EVENTS */

.theme3-design .content-panel {
  background: #ffffff;
  border: 1px solid #e4eee4;
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(30, 95, 55, 0.04);
}

.theme3-design .panel-heading h4 {
  color: #174c32;
  font-weight: 800;
}

.theme3-design .news-item {
  border-color: #e7eee6;
}

.theme3-design .news-date {
  color: #27824a;
}

.theme3-design .news-copy .ant-tag {
  background: #e8f4e9;
  color: #27824a;
  border-radius: 20px;
  border: none;
}

.theme3-design .news-copy strong {
  color: #31583e;
}

.theme3-design .news-arrow {
  color: #e18a36;
}

.theme3-design .event-date {
  background: #fff0df;
  color: #d87922;
  border-radius: 10px;
}

.theme3-design .success-panel {
  background: #f5f9f3;
  border: 1px solid #e4eee4;
  border-radius: 14px;
}

.theme3-design .success-panel h4 {
  color: #174c32;
}

.theme3-design .success-panel a {
  color: #27824a;
}

/* DOWNLOADS */

.theme3-design .downloads-band {
  background: #edf5e9;
  border-color: #dcebd8;
}

.theme3-design .downloads-inner h3 {
  color: #174c32;
  font-weight: 800;
}

.theme3-design .download-links button {
  background: #ffffff;
  border: 1px solid #dcebd8;
  color: #315d3d;
  border-radius: 30px;
  transition: all 0.2s ease;
}

.theme3-design .download-links button:hover {
  border-color: #27824a;
  color: #27824a;
  background: #f7fcf5;
}

/* FOOTER */

.theme3-design .site-footer {
  background: #123d29;
  color: #ffffff;
}

.theme3-design .site-footer h5 {
  color: #f0a04b;
}

.theme3-design .site-footer p {
  color: #c6ddc9;
}

.theme3-design .site-footer a {
  color: #d8e9da;
}

.theme3-design .site-footer a:hover {
  color: #f0a04b;
}

.theme3-design .footer-seal {
  background: #e8f4e9;
  color: #27824a;
  border-radius: 50%;
}

.theme3-design .socials {
  color: #f0a04b;
}

.theme3-design .footer-bottom {
  color: #a9c7ae;
}

/* MODAL */

.theme3-design .modal-title {
  color: #174c32;
  font-weight: 800;
}

/* RESPONSIVE */

@media (max-width: 680px) {
  .theme3-design .hero {
    min-height: 390px;
  }

  .theme3-design .hero h1 {
    font-size: 36px;
  }

  .theme3-design .service-card {
    min-height: 140px;
  }

  .theme3-design .section-heading h2 {
    font-size: 22px;
  }

  .theme3-design .scheme-image {
    height: 145px;
  }

  .theme3-design .crop-card {
    height: 160px;
  }

  .theme3-design .area-grid {
    gap: 15px;
  }

  .theme3-design .downloads-inner {
    gap: 15px;
  }
}

      `}</style>

            <SiteHeader
                onOpenService={setActiveService}
                selectedTheme={selectedTheme}
                setSelectedTheme={setSelectedTheme}
            />
            <Content id="main">
                <Hero />
                <QuickServices onOpenService={setActiveService} />
                <AtGlance />
                <Schemes />
                <CropExplorer />
                <AreaCoverage />
                <NewsEvents />
                <Downloads />
            </Content>
            <SiteFooter />
            <ServiceModal service={activeService} onClose={() => setActiveService(null)} />
        </Layout>
    </ConfigProvider>;
}

